import { Link } from "react-router";
import { ResourceContextProvider, useGetList, useTranslate } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BalanceCard } from "./BalanceCard";
import { MonthlyCashflowChart } from "./MonthlyCashflowChart";
import { TransactionList } from "../transactions/TransactionList";
import { PlanningCalendar } from "./PlanningCalendar";
import { PlannedCashflowChart } from "./PlannedCashflowChart";
import type { AccountBalance } from "../../types";

export const FinanceDashboard = () => {
  const translate = useTranslate();
  const { data, isPending } = useGetList<AccountBalance>("account_balances", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
    filter: { "archived_at@is": null },
  });

  return (
    <div className="max-w-6xl mx-auto py-4 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">
          {translate("finance.dashboard.title", { _: "Финансы" })}
        </h1>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" variant="outline">
            <Link to="/accounts/create">
              {translate("resources.accounts.action.new", { _: "Счёт" })}
            </Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/transactions/create?direction=income">
              {translate("finance.dashboard.addIncome", { _: "Доход" })}
            </Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/transactions/create?direction=expense">
              {translate("finance.dashboard.addExpense", { _: "Расход" })}
            </Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/transfers/create">
              {translate("finance.dashboard.addTransfer", { _: "Перевод" })}
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {isPending ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))
        ) : data?.length ? (
          data.map((bal) => <BalanceCard key={bal.id} balance={bal} />)
        ) : (
          <Card className="col-span-full">
            <CardContent className="py-8 text-center text-muted-foreground">
              {translate("finance.dashboard.noAccounts", {
                _: "Создайте первый счёт",
              })}
            </CardContent>
          </Card>
        )}
      </div>

      <Card>
        <CardContent className="px-4 py-3 space-y-3">
          <h2 className="text-sm font-semibold">
            {translate("finance.dashboard.cashflow", {
              _: "Доходы/расходы по месяцам",
            })}
          </h2>
          <MonthlyCashflowChart />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="px-4 py-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">
              {translate("finance.forecast.title", { _: "Прогноз — календарь планируемых движений" })}
            </h2>
            <Button asChild size="sm" variant="outline">
              <Link to="/payments">
                {translate("finance.forecast.openPlanning", { _: "Перейти в планирование" })}
              </Link>
            </Button>
          </div>
          <PlanningCalendar />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="px-4 py-3 space-y-3">
          <h2 className="text-sm font-semibold">
            {translate("finance.forecast.chartTitle", { _: "Планируемые доходы/расходы по месяцам" })}
          </h2>
          <PlannedCashflowChart />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="px-4 py-3 space-y-3">
          <h2 className="text-sm font-semibold">
            {translate("resources.transactions.name", { smart_count: 2 })}
          </h2>
          <ResourceContextProvider value="transactions">
            <TransactionList />
          </ResourceContextProvider>
        </CardContent>
      </Card>
    </div>
  );
};

FinanceDashboard.path = "/finance";
